import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { UserLocation } from '@apps/main/new-desktop/core/models';
import { isOwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import {
  isHotelIngredientResponseWithStaticInfo,
  isOwnStayIngredientResponseWithStaticInfo,
  isOwnTransportIngredientResponseWithStaticInfo,
  isTransportIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import type { ChronologicalItinerary } from '@libs/trip-planner-interfaces';
import { TransportType } from '@libs/trip-planner-interfaces';
import { ConnectionType } from '@libs/maps';

import type {
  ItineraryLocationSegment,
  ItineraryOwnTransportSegment,
  ItinerarySegment,
  ItinerarySimpleTransportSegmentType,
  LocationSegment,
  TripOverviewSegments,
} from '../../models';
import { ItinerarySegmentType, UnavailabilityReason } from '../../models';

import { getBookableTransportSegment } from './tripOverviewBookableTransportSegmentLogic';
import { getHotelRelatedSegments } from './tripOverviewHotelRelatedSegmentsLogic';
import { getOwnStayRelatedSegments } from './tripOverviewOwnStayRelatedSegmentsLogic';
import { getOwnTransportRelatedSegment } from './tripOverviewOwnTransportRelatedSegmentsLogic';

export function getTripOverviewSegments(
  context: TripOverviewSegments
): ItinerarySegment[] {
  // Chronological itinerary is empty when there is no selectedItineraryWithStaticInfo
  if (!context.chronologicalItinerary?.length) {
    return [];
  }
  const itinerary = context.chronologicalItinerary.reduce(
    (
      segments: ItinerarySegment[],
      itineraryByPeriod: ChronologicalItinerary,
      currentIndex: number
    ) => {
      itineraryByPeriod.ingredients.forEach((ingredient) => {
        const bookableTransportSegment = getBookableTransportSegment(
          ingredient,
          context.getIngredient,
          context.isSuggestedTransport,
          context.ferryTicketCategoryDictionary,
          context.userSelectedFerryTicketCategory,
          context.getFlightAllCarriers,
          context.getFerryAllCarriers,
          context.currency,
          context.selectedDate
        );

        if (bookableTransportSegment) {
          segments.push(bookableTransportSegment);
        }

        const hotelRelatedSegments = getHotelRelatedSegments(
          ingredient,
          context.getIngredient,
          context.isSuggestedHotel,
          context.areThereActivitiesInDestination(ingredient.ingredientId),
          context.getActivitySuggestions(ingredient.ingredientId),
          context.getStaticActivitySuggestions(ingredient.ingredientId),
          context.getIngredientIndex(ingredient.ingredientId),
          context.moneyFormanterFn,
          itineraryByPeriod,
          context.isDateLocationSegmentExperiment,
          context.selectedDate
        );
        segments.push(...hotelRelatedSegments);

        if (isOwnTransportIngredientResponseWithStaticInfo(ingredient)) {
          const hotelRelatedSegment = itineraryByPeriod.ingredients[1];
          const isLastSegment =
            ingredient.ingredientId ===
            itineraryByPeriod.ingredients[
              itineraryByPeriod.ingredients.length - 1
            ]?.ingredientId;

          let ownTransportRelatedSegments:
            | ItineraryOwnTransportSegment
            | undefined;

          if (
            hotelRelatedSegment &&
            (isHotelIngredientResponseWithStaticInfo(hotelRelatedSegment) ||
              isOwnStayIngredientResponseWithStaticInfo(hotelRelatedSegment))
          ) {
            ownTransportRelatedSegments = getOwnTransportRelatedSegment(
              ingredient,
              hotelRelatedSegment,
              context.getIngredientIndex(ingredient.ingredientId),
              context.getIngredient,
              context.calculatedRoute,
              getShouldAddTransportSegment(
                itineraryByPeriod,
                context.chronologicalItinerary[currentIndex - 1]
              ),
              !context.isSmallDevice,
              context.isUserFromBookingPage,
              context.isUserFromStena,
              context.hasUserSelectedTransport,
              false
            );
          } else if (isLastSegment && context.endLocationIngredient) {
            ownTransportRelatedSegments = getOwnTransportRelatedSegment(
              ingredient,
              context.endLocationIngredient,
              context.getIngredientIndex(ingredient.ingredientId),
              context.getIngredient,
              context.calculatedRoute,
              getShouldAddTransportSegment(
                itineraryByPeriod,
                context.chronologicalItinerary[currentIndex - 1]
              ),
              !context.isSmallDevice,
              context.isUserFromBookingPage,
              context.isUserFromStena,
              context.hasUserSelectedTransport,
              true
            );
          }
          if (ownTransportRelatedSegments) {
            segments.push(ownTransportRelatedSegments);
          }
        }

        const ownStayRelatedSegments = getOwnStayRelatedSegments(
          ingredient,
          context.getIngredient,
          context.getIngredientIndex(ingredient.ingredientId),
          context.areThereActivitiesInDestination(ingredient.ingredientId),
          context.getActivitySuggestions(ingredient.ingredientId),
          context.getStaticActivitySuggestions(ingredient.ingredientId),
          context.moneyFormanterFn,
          itineraryByPeriod,
          context.isDateLocationSegmentExperiment,
          context.selectedDate
        );
        segments.push(...ownStayRelatedSegments);
      });
      if (isFeatureToggleActive('show-availability-errors')) {
        addErrorReasons(segments);
      }

      return segments;
    },
    []
  );

  const firstIngredient = context.chronologicalItinerary[0]?.ingredients[0];

  if (firstIngredient) {
    const firstRecipeIngredient = context.getIngredient(
      firstIngredient.ingredientId
    );
    if (
      context.startLocation &&
      firstRecipeIngredient &&
      isOwnTransportIngredient(firstRecipeIngredient) &&
      firstRecipeIngredient.ownTransportType !== TransportType.None
    ) {
      itinerary.unshift(
        getTripOverviewLocationSegment(
          context.startLocation,
          ItinerarySegmentType.StartLocation
        )
      );
    }
  }

  const endLocationSegments = getEndLocationSegments(
    context.endLocation,
    context.endLocationIngredient,
    context.chronologicalItinerary
  );
  itinerary.push(...endLocationSegments);
  return itinerary;
}

export function getEndLocationSegments(
  endLocation: UserLocation | undefined,
  endLocationIngredient: HotelIngredient | undefined,
  chronologicalItinerary: ChronologicalItinerary[] = []
): ItinerarySegment[] {
  const segments: ItinerarySegment[] = [];

  const lastChronologicalItinerary =
    chronologicalItinerary[chronologicalItinerary.length - 1];

  const lastItineraryIngredient =
    lastChronologicalItinerary &&
    lastChronologicalItinerary.ingredients[
      lastChronologicalItinerary.ingredients.length - 1
    ];
  if (
    endLocation &&
    endLocationIngredient &&
    lastItineraryIngredient &&
    isOwnTransportIngredientResponseWithStaticInfo(lastItineraryIngredient) &&
    lastItineraryIngredient.ownTransportType !== TransportType.None
  ) {
    segments.push(
      getTripOverviewLocationSegment(
        endLocation,
        ItinerarySegmentType.EndLocation
      )
    );
  }
  return segments;
}

export function getTripOverviewLocationSegment(
  location: UserLocation,
  segmentType:
    | ItinerarySegmentType.StartLocation
    | ItinerarySegmentType.EndLocation
): ItineraryLocationSegment {
  return {
    segmentType,
    location: getLocationSegmentDisplay(location),
  };
}

function getLocationSegmentDisplay(location: UserLocation): LocationSegment {
  return {
    name: location.name,
    country: location.country,
  };
}

export function getTransportTypeForItinerarySimpleTransportSegment(
  segmentType: ItinerarySimpleTransportSegmentType
): TransportType {
  switch (segmentType) {
    case ItinerarySegmentType.Car:
      return TransportType.Car;
    case ItinerarySegmentType.Walk:
      return TransportType.Walk;
    case ItinerarySegmentType.Cycle:
      return TransportType.Bicycle;
    default:
      throw new Error('No simple transport in itinerary segment');
  }
}

export function mapTransportTypeToConnectionType(
  transportType: TransportType
): ConnectionType {
  switch (transportType) {
    case TransportType.Car:
      return ConnectionType.CAR;
    case TransportType.Walk:
      return ConnectionType.WALK;
    case TransportType.Bicycle:
      return ConnectionType.BICYCLE;
    case TransportType.Flight:
      return ConnectionType.FLIGHT;
    case TransportType.Ferry:
      return ConnectionType.FERRY;
    default:
      return ConnectionType.NONE;
  }
}

export function getShouldAddTransportSegment(
  itineraryByPeriod: ChronologicalItinerary,
  previousChronologicalItinerary: ChronologicalItinerary | undefined
): boolean {
  const hasAdvancedTransportInPeriod = itineraryByPeriod.ingredients.some(
    isTransportIngredientResponseWithStaticInfo
  );

  if (hasAdvancedTransportInPeriod) {
    return false;
  }

  if (
    previousChronologicalItinerary &&
    previousChronologicalItinerary.ingredients.length === 1
  ) {
    const ingredient = previousChronologicalItinerary.ingredients[0];

    return (
      !!ingredient && !isTransportIngredientResponseWithStaticInfo(ingredient)
    );
  }
  return true;
}

function addErrorReasons(segment: ItinerarySegment[]): void {
  segment.forEach((item) => {
    if ('reasons' in item) {
      item.reasons = [
        UnavailabilityReason.SelectedDate,
        UnavailabilityReason.SelectedProduct,
        UnavailabilityReason.SelectedFilters,
        UnavailabilityReason.Suggested,
      ];
    }
  });
}
